'use client';

import { useState, useEffect } from 'react';
import { Play, Eye, Clock, AlertCircle } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';
import Image from 'next/image';
import { TrendingUp } from 'lucide-react';
import { FormattedStream, KICK_USERNAME } from '@/types/kick';

// Thumbnails for older VODs 403 on images.kick.com, so fall back instead of rendering a broken image.
function StreamThumbnail({ src, alt }: { src?: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
        <Play className="w-16 h-16 text-green-400/30" />
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={400}
      height={225}
      onError={() => setFailed(true)}
      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      draggable="false"
    />
  );
}

export default function KickStreamsSection() {
  const [streams, setStreams] = useState<FormattedStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchStreams = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await fetch('/api/kick-streams');
        const result = await response.json();

        if (!result.streams || result.streams.length === 0) {
          setError(true);
          setStreams([]);
          return;
        }

        setStreams(result.streams);
      } catch (err) {
        console.error('⚠️ Error fetching streams:', err);
        setError(true);
        setStreams([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStreams();
  }, []);

  if (loading) {
    return (
      <div className="py-16 px-4">
        <h2 className="text-2xl md:text-4xl font-mono font-bold text-center mb-4 text-green-400">
          <span className="inline-block animate-pulse">{'['}</span> RECENT STREAMS <span className="inline-block animate-pulse">{']'}</span>
        </h2>
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-400"></div>
        </div>
      </div>
    );
  }

  if (error || streams.length === 0) {
    return (
      <div className="py-16 px-4">
        <h2 className="text-2xl md:text-4xl font-mono font-bold text-center mb-4 text-green-400">
          <span className="inline-block animate-pulse">{'['}</span> RECENT STREAMS <span className="inline-block animate-pulse">{']'}</span>
        </h2>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <AlertCircle className="w-16 h-16 text-yellow-400 mb-4" />
          <p className="text-yellow-400 font-mono text-lg mb-2">No recent videos available</p>
          <p className="text-gray-400 font-mono text-sm mb-6">Check back later for new streams</p>
          <a
            href={`https://kick.com/${KICK_USERNAME}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-2 font-mono border-2 border-green-400 text-green-300 rounded-lg hover:bg-green-400/10 transition-all"
          >
            VISIT CHANNEL
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 px-4">
      <h2 className="text-2xl md:text-4xl font-mono font-bold text-center mb-4 text-green-400">
        <span className="inline-block animate-pulse">{'['}</span> RECENT STREAMS <span className="inline-block animate-pulse">{']'}</span>
      </h2>
      <p className="text-center text-green-300 font-mono mb-12">
        Check out the latest action from Kick
      </p>

      <Swiper
        modules={[Pagination]}
        spaceBetween={20}
        slidesPerView={1}
        pagination={{ clickable: true }}
        breakpoints={{
          640: { slidesPerView: 2 },
          1024: { slidesPerView: 4 },
        }}
        className="max-w-6xl mx-auto"
      >
        {streams.slice(0, 8).map((stream) => (
          <SwiperSlide key={stream.id}>
            <a
              href={stream.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden transition-all duration-200 bg-black/40"
            >
              <div className="relative aspect-video overflow-hidden bg-black/40 rounded-tl-xl rounded-tr-xl">
                <StreamThumbnail src={stream.thumbnail} alt={stream.title} />

                <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs font-mono text-green-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {stream.duration ? stream.duration : "LIVE"}
                </div>
              </div>

              <div className="p-4 backdrop-blur shadow-sm rounded-bl-xl rounded-br-xl">
                <h3 className="text-sm font-mono font-bold text-gray-200 mb-2 line-clamp-2 group-hover:text-green-400 transition-colors">
                  {stream.title}
                </h3>
                <div className="flex items-center justify-between text-xs text-gray-400 font-mono">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {stream.views}
                  </span>
                  <span>{stream.date}</span>
                </div>
              </div>
            </a>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="flex justify-center mt-12">
        <a
          href={`https://kick.com/${KICK_USERNAME}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 hover: transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden">
          <TrendingUp className="inline-block w-5 h-5 mr-2" />
          VIEW ALL STREAMS
        </a>
      </div>
    </div>
  );
}
